import { Injectable } from '@angular/core';
import { VerifyChildPostcodeTicket } from '@modules/checkout-store';
import {
  BookingSummary,
  BookingSummaryItem,
  CustomerTypeIds,
  DeliveryMethod,
  IAddress,
  IChildEducationDetails,
  ICommunicationPreferences,
  ICustomerDetails,
  IFormValidationRules,
  IMTicketDetails,
  IStudentDetails,
  ISwiftCardInfoDetails,
  IValidationRules,
  SwiftCardState,
} from '@nx-ng/types';

import { SWIFT_CARD_NUMBER_REGEX } from '@nx-ng/util/validate-swift-card-number';
import {
  EMAIL_REGEX,
  getCurrentMonthUTCDate,
  getNowUTCDate,
  getYesterdayUTCDate,
  MOBILE_REGEX,
  NAME_REGEX,
  POSTCODE_REGEX,
  valueMeetsRules,
} from '@nx-ng/util';
import { SwiftMinDateService } from '.';
import { ValidationService } from './validation.service';
import { DEFAULT_TIMEZONE } from '@nx-ng/constants';
import { utcToZonedTime } from 'date-fns-tz';
import { TicketApplicantState } from '@nx-ng/types/previous-applicant-details';
import { ValidationErrors } from '@angular/forms';

//API breaking with earlier dates
const DATE_1800 = utcToZonedTime(Date.UTC(1800, 0, 1), DEFAULT_TIMEZONE);

@Injectable({
  providedIn: 'root',
})
export class BookingSummaryValidationService extends ValidationService<BookingSummary> {
  constructor(private swiftMinDateService: SwiftMinDateService) {
    super();
  }

  additionalValidation(bookingSummary?: BookingSummary): boolean {
    if (
      !bookingSummary ||
      !bookingSummary.items ||
      bookingSummary.items.length === 0
    ) {
      return false;
    }

    return true;
  }

  customerDateOfBirthIsValid = (item: BookingSummaryItem): boolean => {
    const rules = this.determineBookingItemCustomerDetailsRules(item);
    return valueMeetsRules(
      item.customerDetails?.dateOfBirth,
      rules?.dateOfBirth as IValidationRules | undefined
    );
  };

  billingPostCodeIsValid = (bookingSummary: BookingSummary): boolean => {
    const rules = this.determineBillingAddressRules(bookingSummary);
    const addressRules = rules.address as IFormValidationRules<
      Partial<IAddress>
    >;

    return valueMeetsRules(
      bookingSummary.billingAddress?.address?.postCode,
      addressRules.postCode as IValidationRules | undefined
    );
  };

  determineCommunicationPreferencesRules(
    value?: BookingSummary
  ): IFormValidationRules<Partial<ICommunicationPreferences>> {
    return {
      guardianConsent: {
        requiredTrue:
          value?.items.some(
            (item: BookingSummaryItem) =>
              item.customerType === CustomerTypeIds.Child
          ) || false,
      },
      agreeToTsAndCs: {
        requiredTrue: true,
      },
    };
  }

  determineBillingAddressRules(
    value?: BookingSummary
  ): IFormValidationRules<Partial<ICustomerDetails>> {
    const postCodeVerifyRequired =
      value?.items?.some(
        (item: BookingSummaryItem) =>
          item.customerType === CustomerTypeIds.Child
      ) || false;

    return {
      address: {
        address1: {
          required: true,
        },
        town: {
          required: true,
        },
        postCode: {
          required: true,
          pattern: new RegExp(POSTCODE_REGEX),
        },
      },
      firstName: {
        required: true,
      },
      lastName: {
        required: true,
      },
      postcodeValidForChildTickets: {
        requiredTrue: postCodeVerifyRequired,
      },
    };
  }

  determineBookingItemCustomerDetailsRules(
    value: BookingSummaryItem
  ): IFormValidationRules<Partial<ICustomerDetails>> | undefined {
    return {
      firstName: {
        required:
          value.ticketIsForApplicant === TicketApplicantState.SomeoneElse,
        pattern:
          value.ticketIsForApplicant === TicketApplicantState.SomeoneElse
            ? NAME_REGEX
            : undefined,
      },
      lastName: {
        required:
          value.ticketIsForApplicant === TicketApplicantState.SomeoneElse,
        pattern:
          value.ticketIsForApplicant === TicketApplicantState.SomeoneElse
            ? NAME_REGEX
            : undefined,
      },
      dateOfBirth: {
        required: true,
        minDate: this.determineMinDateOfBirthForTicket(value.ticket.maxAgeBand),
        maxDate: this.determineMaxDateOfBirthForTicket(value.ticket.minAgeBand),
      },
    };
  }

  determineMinDateOfBirthForTicket = (
    maxAgeBand?: number
  ): Date | undefined => {
    const today = utcToZonedTime(getNowUTCDate(), DEFAULT_TIMEZONE);

    if (!maxAgeBand) {
      return undefined;
    }

    const minDate = new Date(
      today.getFullYear() - maxAgeBand - 1,
      today.getMonth(),
      today.getDate()
    );

    //If maxAgeband is 18 this will give us the day before their 19th birthday
    minDate.setDate(minDate.getDate() + 1);

    return minDate;
  };

  determineMaxDateOfBirthForTicket = (
    minAgeBand?: number
  ): Date | undefined => {
    if (!minAgeBand) {
      const yesterday = utcToZonedTime(getYesterdayUTCDate(), DEFAULT_TIMEZONE);
      return yesterday;
    }
    const today = utcToZonedTime(getNowUTCDate(), DEFAULT_TIMEZONE);

    return new Date(
      today.getFullYear() - minAgeBand,
      today.getMonth(),
      today.getDate()
    );
  };

  determineMTicketRules(
    item: BookingSummaryItem
  ): IFormValidationRules<Partial<IMTicketDetails>> {
    return {
      email: {
        required: true,
        pattern: EMAIL_REGEX,
      },
      mobile: {
        required: true,
        pattern: MOBILE_REGEX,
      },
      startDate: {
        required: true,
        minDate: utcToZonedTime(getNowUTCDate(), DEFAULT_TIMEZONE),
      },
      verified: {
        requiredTrue: true,
      },
    };
  }

  determineSwiftRules(
    item: BookingSummaryItem,
    bookingSummary: BookingSummary
  ): IFormValidationRules<Partial<ISwiftCardInfoDetails>> {
    return {
      swiftCardState: {
        required: true,
      },
      cardNumber: {
        required: item.swiftCardDetails.swiftCardState !== SwiftCardState.New,
        pattern:
          item.swiftCardDetails.swiftCardState === SwiftCardState.Register
            ? SWIFT_CARD_NUMBER_REGEX
            : undefined,
        satisfies: (): ValidationErrors | null => {
          if (item.swiftCardDetails.swiftCardState === SwiftCardState.Register) {
            const existingSwiftCardWithSameNumber = bookingSummary.items.filter((summaryItem) =>
                summaryItem.swiftCardDetails.swiftCardState === SwiftCardState.Register &&
                summaryItem.swiftCardDetails.cardNumber === item.swiftCardDetails.cardNumber
            );

            if (existingSwiftCardWithSameNumber.length > 1) {
              return { unique: true }; // not unique - There should be only one swift card with same number in summary
            }
          }

          return null; // unique
        }
      },
      activationDate: {
        required: true,
        minDate:
          item.swiftCardDetails.swiftCardState === SwiftCardState.New
            ? this.swiftMinDateService.getMinDateForNewSwiftCard()
            : this.swiftMinDateService.getMinDateForExistingSwiftCard(),
      },
      verified: {
        requiredTrue:
          item.swiftCardDetails.swiftCardState === SwiftCardState.Register,
      },
      photo: {
        required: item.swiftCardDetails.swiftCardState === SwiftCardState.New,
      },
      newCardDeliveryAddress: {
        address1: {
          required: item.swiftCardDetails.swiftCardState === SwiftCardState.New,
        },
        town: {
          required: item.swiftCardDetails.swiftCardState === SwiftCardState.New,
        },
        postCode: {
          required: item.swiftCardDetails.swiftCardState === SwiftCardState.New,
          pattern:
            item.swiftCardDetails?.swiftCardState === SwiftCardState.New
              ? new RegExp(POSTCODE_REGEX)
              : undefined,
        },
      },
    };
  }

  determineChildEducationRules(
    item: BookingSummaryItem
  ): IFormValidationRules<Partial<IChildEducationDetails>> | undefined {
    if (item.customerType !== CustomerTypeIds.Child) {
      return undefined;
    }
    return {
      employment: {
        required: item.childEducationDetails?.occupation === 'Voluntary',
      },
      training: {
        required: item.childEducationDetails?.occupation === 'Trainee',
      },
      education: {
        required: item.childEducationDetails?.occupation === 'FullTime',
      },
      address: {
        address1: {
          required: true,
        },
        town: {
          required: true,
        },
        postCode: {
          required: true,
          pattern: new RegExp(POSTCODE_REGEX),
        },
      },
    };
  }

  determineStudentDetailsRules(
    item: BookingSummaryItem
  ): IFormValidationRules<Partial<IStudentDetails>> | undefined {
    if (item.customerType !== CustomerTypeIds.Student) {
      return undefined;
    }
    return {
      studentId: {
        required: true,
      },
      institution: {
        required: true,
      },
      course: {
        required: true,
      },
      courseEndDate: {
        required: true,
        minDate: utcToZonedTime(getCurrentMonthUTCDate(), DEFAULT_TIMEZONE),
      },
    };
  }

  rulesFromDefinition(
    value?: BookingSummary
  ): IFormValidationRules<BookingSummary> {
    return {
      communicationPreferences:
        this.determineCommunicationPreferencesRules(value),
      billingAddress: this.determineBillingAddressRules(value),
      items:
        value?.items.map((item) => {
          return {
            customerDetails:
              this.determineBookingItemCustomerDetailsRules(item),
            mTicketDetails:
              item.selectedDeliveryMethod === DeliveryMethod.MTicket
                ? this.determineMTicketRules(item)
                : undefined,
            swiftCardDetails:
              item.selectedDeliveryMethod === DeliveryMethod.Swift
                ? this.determineSwiftRules(item, value)
                : undefined,
            childEducationDetails: this.determineChildEducationRules(item),
            studentDetails: this.determineStudentDetailsRules(item),
            selectedDeliveryMethod: {
              required: true,
            },
            basketItemId: {
              required: true,
            },
            ticketIsForApplicant: {
              required: true,
              satisfies: (): ValidationErrors | null => {
                if (item.ticketIsForApplicant === TicketApplicantState.ForMyself) {
                  const existingTicketsForMyself = value.items.filter((summaryItem) =>
                      summaryItem.ticket.id === item.ticket.id &&
                      summaryItem.ticketIsForApplicant === TicketApplicantState.ForMyself &&
                      summaryItem.quantityIndex !== item.quantityIndex
                  );

                  if (existingTicketsForMyself.length > 0) {
                    return { unique: true }; // not unique - There should be maximum 1 ticket of same id for "Myself"
                  }
                }

                return null; // unique
              }
            },
          };
        }) || [],
    };
  }

  determineChildTicketsToVerify(
    bookingSummary: BookingSummary
  ): VerifyChildPostcodeTicket[] {
    return (bookingSummary?.items || [])
      .filter((item: BookingSummaryItem): boolean =>
        this.childTicketCanBeVerified({ bookingSummary, item })
      )
      .map((item: BookingSummaryItem): VerifyChildPostcodeTicket => {
        return this.formatVerifyChildPostcodeTicket({
          bookingSummaryItem: item,
          bookingSummary,
        });
      });
  }

  childTicketCanBeVerified({
    bookingSummary,
    item,
  }: {
    bookingSummary: BookingSummary;
    item: BookingSummaryItem;
  }) {
    return (
      this.billingPostCodeIsValid(bookingSummary) &&
      item.customerType === CustomerTypeIds.Child &&
      item.customerDetails?.dateOfBirth !== undefined &&
      this.customerDateOfBirthIsValid(item) &&
      item.customerDetails.dateOfBirth.getTime() > DATE_1800.getTime()
    );
  }

  formatVerifyChildPostcodeTicket({
    bookingSummaryItem,
    bookingSummary,
  }: {
    bookingSummaryItem: BookingSummaryItem;
    bookingSummary: BookingSummary;
  }) {
    return {
      ticketID: bookingSummaryItem.basketItemId,
      dateOfBirth:
        bookingSummaryItem.customerDetails?.dateOfBirth || new Date(),
      postCode: bookingSummary.billingAddress?.address?.postCode || '',
    };
  }

  checkIfChildTicketToBeVerified({
    bookingSummary,
    itemIndex,
  }: {
    bookingSummary?: BookingSummary;
    itemIndex: number;
  }): VerifyChildPostcodeTicket | undefined {
    const bookingSummaryItem = bookingSummary?.items[itemIndex];
    if (!bookingSummaryItem) {
      return undefined;
    }

    if (
      this.childTicketCanBeVerified({
        bookingSummary,
        item: bookingSummaryItem,
      })
    ) {
      return this.formatVerifyChildPostcodeTicket({
        bookingSummaryItem,
        bookingSummary,
      });
    }
    return undefined;
  }

  ageRangeError(item: BookingSummaryItem): boolean {
    if (!item.customerDetails?.dateOfBirth) {
      return false;
    }

    const rules = this.determineBookingItemCustomerDetailsRules(
      item
    ) as IFormValidationRules<Partial<ICustomerDetails>>;
    const dobRules = rules?.dateOfBirth as IValidationRules;
    const { maxDate, minDate } = dobRules;

    const dobAsTime = item.customerDetails.dateOfBirth.getTime();

    if (
      (maxDate && dobAsTime > maxDate.getTime()) ||
      (minDate && dobAsTime < minDate?.getTime())
    ) {
      return true;
    }

    return false;
  }
}
